/**
 * \u73bb\u7483\u5b9d\u77f3\u6e32\u67d3\u5668\uff08Canvas 2D\uff09
 *
 * \u8fc1\u79fb\u81ea design/art/02-board-mock.html \u7684 gem() \u4e03\u6b65\u7ed8\u5236\u6cd5\uff1a
 *   1. \u571f\u661f\u80cc\u4fa7\u73af\u5e26
 *   2. \u5916\u53d1\u5149\u6655
 *   3. \u672c\u4f53\uff08\u4e3b\u5149\u5411\u6e10\u53d8\uff09
 *   4. \u5185\u90e8\u7ed3\u6784\uff08\u5185\u542b\u7269 / \u68f1\u7ebf / \u51a0\u90e8\u53f0\u9762\uff0c\u88c1\u526a\u5728\u8f6e\u5ed3\u5185\uff09
 *   5. \u8fb9\u7f18\u6697\u89d2
 *   6. \u8f6e\u5ed3\u5149
 *   7. \u955c\u9762\u9ad8\u5149
 *   8. \u571f\u661f\u524d\u4fa7\u73af\u5e26
 *
 * \u94c1\u5f8b\uff1a\u4e3b\u5149\u6052\u5b9a\u4e3a\u300c\u5de6\u4e0a 45\u00b0\u3001\u4ef0\u89d2 35\u00b0\u300d\uff0c\u8865\u5149\u300c\u53f3\u4e0b 15%\u300d\u3002
 * \u4e25\u7981\u7eaf #FFFFFF \u5927\u9762\u79ef\u9ad8\u5149\u3002
 */

import { GEM_SPECS, getGemSpec, type ColorKey } from './palette';
import { silhouette, ellipsePts, type Pt } from './geometry';

/** \u4e3b\u5149\u65b9\u5411\uff1a\u5de6\u4e0a 45\u00b0\uff08\u5f52\u4e00\u5316\u5411\u91cf\uff09 */
const KEY_LIGHT = { x: -Math.SQRT1_2, y: -Math.SQRT1_2 };
/** \u8865\u5149\u65b9\u5411\uff1a\u53f3\u4e0b */
const FILL_LIGHT = { x: Math.SQRT1_2, y: Math.SQRT1_2 };

/** \u516d\u8272\u9884\u70d8\u7119\u8d34\u56fe\uff08Tier D \u5151\u5e95\uff0c\u94c1\u5f8b\u4e09\uff1a\u540c\u578b\u68cb\u5b50\u5171\u7528\u7f13\u5b58\uff09 */
const spriteCache = new Map<string, HTMLCanvasElement>();

export interface GemDrawOpts {
  /** \u7403\u5fc3 X\uff08\u753b\u5e03\u5750\u6807\uff09 */
  cx: number;
  /** \u7403\u5fc3 Y\uff08\u753b\u5e03\u5750\u6807\uff09 */
  cy: number;
  /** \u5916\u63a5\u534a\u5f84 */
  r: number;
  /** \u6574\u4f53\u900f\u660e\u5ea6\uff08\u7f8e\u672f\u5723\u7ecf \u00a72.1\uff1a\u672c\u4f53 0.90\u20130.94\uff09 */
  alpha?: number;
}

/** \u4e00\u6b21\u6027\u521d\u59cb\u5316\u5168\u90e8\u9884\u70d8\u7119\u56fe\u7247\uff08\u542f\u52a8\u65f6\u8c03\u7528\u4e00\u6b21\uff09 */
export function warmupSprites(cellSize: number, dpr: number): void {
  for (const key of Object.keys(GEM_SPECS) as ColorKey[]) {
    const id = `${key}@${cellSize}@${dpr}`;
    if (spriteCache.has(id)) continue;
    const size = Math.ceil(cellSize * dpr);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    ctx.scale(dpr, dpr);
    drawGem(ctx, key, { cx: cellSize / 2, cy: cellSize / 2, r: cellSize * 0.44 });
    spriteCache.set(id, canvas);
  }
}

/** \u53d6\u9884\u70d8\u7119\u8d34\u56fe\uff1b\u672a\u70d8\u7119\u8fd4\u56de null */
export function getSprite(key: ColorKey, cellSize: number, dpr: number): HTMLCanvasElement | null {
  return spriteCache.get(`${key}@${cellSize}@${dpr}`) ?? null;
}

/** 清空全部预烘焙贴图（色盲模式切换时调用，强制下一帧重烘焙，S12 E5 即时生效） */
export function clearSpriteCache(): void {
  spriteCache.clear();
}

/**
 * \u7ed8\u5236\u4e00\u9897\u73bb\u7483\u5b9d\u77f3\u3002
 * \u8c03\u7528\u65b9\uff1a\u76f4\u63a5\u4f20\u76ee\u6807 ctx\uff08\u5750\u6807\u5df2\u662f\u8bbe\u5907\u50cf\u7d20\uff09\uff0c\u6216\u5148 translate \u5230\u683c\u5b50\u5de6\u4e0a\u89d2\u3002
 */
export function drawGem(
  ctx: CanvasRenderingContext2D,
  key: ColorKey,
  opts: GemDrawOpts,
): void {
  const s = getGemSpec(key);
  const { cx, cy, r } = opts;
  const alpha = opts.alpha ?? 0.92;

  ctx.save();
  ctx.globalAlpha = alpha;

  // \u2500\u2500 0. \u571f\u661f\uff1a\u80cc\u4fa7\u73af\u5e26\u5148\u753b\uff08\u5728\u672c\u4f53\u4e4b\u540e\uff09
  if (key === 'saturn') drawSaturnRing(ctx, cx, cy, r, 'back');

  // \u2500\u2500 1. \u5916\u53d1\u5149\u6655
  drawGlow(ctx, cx, cy, r, s.mid);

  // \u2500\u2500 2. \u672c\u4f53\uff1a\u4e3b\u5149\u5411\u6e10\u53d8
  const outline = silhouette(s.shape, cx, cy, r);
  const bodyGrad = ctx.createLinearGradient(
    cx + KEY_LIGHT.x * r, cy + KEY_LIGHT.y * r,
    cx - KEY_LIGHT.x * r, cy - KEY_LIGHT.y * r,
  );
  bodyGrad.addColorStop(0, lighten(s.mid, 0.30));
  bodyGrad.addColorStop(0.45, s.mid);
  bodyGrad.addColorStop(1, s.dark);
  tracePoly(ctx, outline);
  ctx.fillStyle = bodyGrad;
  ctx.fill();

  // \u2500\u2500 3. \u5185\u90e8\u7ed3\u6784\uff08\u88c1\u526a\u5728\u8f6e\u5ed3\u5185\uff09
  ctx.save();
  tracePoly(ctx, outline);
  ctx.clip();
  drawInterior(ctx, key, cx, cy, r);
  ctx.restore();

  // \u2500\u2500 4. \u8fb9\u7f18\u6697\u89d2
  const vig = ctx.createRadialGradient(cx, cy, r * 0.55, cx, cy, r);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, hexA(s.dark, 0.55));
  tracePoly(ctx, outline);
  ctx.fillStyle = vig;
  ctx.fill();

  // \u2500\u2500 5. \u8f6e\u5ed3\u5149
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  tracePoly(ctx, outline);
  ctx.strokeStyle = hexA(s.rim, 0.55);
  ctx.lineWidth = Math.max(1, r * 0.035);
  ctx.stroke();
  ctx.restore();

  // \u2500\u2500 6. \u955c\u9762\u9ad8\u5149\uff08\u4e3b\u9ad8\u5149 \u5de6\u4e0a 45\u00b0 + \u6b21\u9ad8\u5149 \u53f3\u4e0b 15%\uff09
  drawSpecular(ctx, cx, cy, r, s.spec);

  // \u2500\u2500 7. \u571f\u661f\uff1a\u524d\u4fa7\u73af\u5e26\u540e\u753b
  if (key === 'saturn') drawSaturnRing(ctx, cx, cy, r, 'front');

  ctx.restore();
}

/** \u5185\u90e8\u7ed3\u6784\uff1a\u6839\u636e facet \u5207\u9762\u5bc6\u5ea6\u4e09\u6863\u5206\u652f\uff08\u91d1\u661f\u5bc6 / \u5730\u7403\u758f / \u5176\u4f59\u4e2d\uff09 */
function drawInterior(
  ctx: CanvasRenderingContext2D,
  key: ColorKey,
  cx: number, cy: number, r: number,
): void {
  const s = getGemSpec(key);

  // \u51a0\u90e8\u53f0\u9762\uff08\u4e03\u8fb9\u5f62\u8fd1\u4f3c\uff0c\u534a\u5f84 \u2248 0.46R\uff09
  const table = pts7(cx, cy - r * 0.06, r * 0.46);
  ctx.save();
  ctx.globalAlpha = 0.5;
  tracePoly(ctx, table);
  const tg = ctx.createLinearGradient(cx - r * 0.4, cy - r * 0.5, cx + r * 0.4, cy + r * 0.2);
  tg.addColorStop(0, hexA(s.core, 0.85));
  tg.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = tg;
  ctx.fill();
  ctx.restore();

  // \u4ead\u90e8\u68f1\u7ebf\uff1a\u4ece\u51a0\u90e8\u53f0\u9762\u5411\u4e0b\u5c04
  const facetCount = facetCountOf(key);
  ctx.save();
  ctx.globalAlpha = 0.32;
  ctx.strokeStyle = hexA(s.core, 0.9);
  ctx.lineWidth = Math.max(0.7, r * 0.02);
  for (let i = 0; i < facetCount; i++) {
    const a = -Math.PI / 2 + (i * Math.PI * 2) / facetCount;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * r * 0.3, cy + Math.sin(a) * r * 0.3 - r * 0.05);
    ctx.lineTo(cx + Math.cos(a) * r * 0.92, cy + Math.sin(a) * r * 0.92);
    ctx.stroke();
  }
  ctx.restore();

  // \u4e3b\u9898\u5185\u542b\u7269
  switch (key) {
    case 'mercury': drawMercuryIncl(ctx, cx, cy, r, s); break;
    case 'venus': drawVenusCore(ctx, cx, cy, r, s); break;
    case 'earth': drawEarthLand(ctx, cx, cy, r, s); break;
    case 'mars': drawMarsCap(ctx, cx, cy, r, s); break;
    case 'saturn': drawSaturnBands(ctx, cx, cy, r, s); break;
    case 'neptune': drawNeptuneSpot(ctx, cx, cy, r, s); break;
  }
}

/** \u5207\u9762\u5bc6\u5ea6\u4e09\u6863\uff1a\u91d1\u661f\u5bc6 12 \u9053 / \u6d77\u738b\u661f\u4e2d 6 \u9053 / \u5730\u7403\u758f 1 \u9053 */
function facetCountOf(key: ColorKey): number {
  if (key === 'venus') return 12;
  if (key === 'earth') return 1;
  return 6;
}

/** \u5916\u53d1\u5149\u6655\uff08\u534a\u900f\u660e\u5916\u6dc0\uff09 */
function drawGlow(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, color: string): void {
  ctx.save();
  const g = ctx.createRadialGradient(cx, cy, r * 0.7, cx, cy, r * 1.35);
  g.addColorStop(0, hexA(color, 0.30));
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 1.35, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** \u4e3b\u9ad8\u5149 + \u6b21\u9ad8\u5149\uff08\u4e25\u7981\u7eaf\u767d\u5927\u9762\u79ef\uff0c\u6545\u7b2c\u4e00\u5c42\u53ea\u5360\u5c0f\u9762\u79ef\uff09 */
function drawSpecular(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, spec: string): void {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  // \u4e3b\u9ad8\u5149\uff1a\u5de6\u4e0a\u659c 45\u00b0\u7684\u7a84\u957f\u692d\u5706
  const major = ellipsePts(cx + KEY_LIGHT.x * r * 0.42, cy + KEY_LIGHT.y * r * 0.42, r * 0.30, r * 0.13, 32);
  const mg = ctx.createRadialGradient(
    cx + KEY_LIGHT.x * r * 0.42, cy + KEY_LIGHT.y * r * 0.42, 0,
    cx + KEY_LIGHT.x * r * 0.42, cy + KEY_LIGHT.y * r * 0.42, r * 0.30,
  );
  mg.addColorStop(0, hexA(spec, 0.72));
  mg.addColorStop(0.5, hexA(spec, 0.22));
  mg.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.save();
  ctx.translate(cx + KEY_LIGHT.x * r * 0.42, cy + KEY_LIGHT.y * r * 0.42);
  ctx.rotate(-Math.PI / 4);
  tracePoly(ctx, ellipsePts(0, 0, r * 0.30, r * 0.13, 32));
  ctx.fillStyle = mg;
  ctx.fill();
  ctx.restore();

  // \u6b21\u9ad8\u5149\uff1a\u53f3\u4e0b 15% \u5f3a\u5ea6
  const fg = ctx.createRadialGradient(
    cx + FILL_LIGHT.x * r * 0.55, cy + FILL_LIGHT.y * r * 0.55, 0,
    cx + FILL_LIGHT.x * r * 0.55, cy + FILL_LIGHT.y * r * 0.55, r * 0.34,
  );
  fg.addColorStop(0, hexA(spec, 0.15));
  fg.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.beginPath();
  ctx.arc(cx + FILL_LIGHT.x * r * 0.55, cy + FILL_LIGHT.y * r * 0.55, r * 0.34, 0, Math.PI * 2);
  ctx.fillStyle = fg;
  ctx.fill();

  ctx.restore();
  void major;
}

/** \u571f\u661f\u73af\u5e26\uff1arotate(-18\u00b0)\uff0crx = 1.42R\uff0cry = 0.40R */
function drawSaturnRing(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  side: 'back' | 'front',
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((-18 * Math.PI) / 180);

  const rx = r * 1.42;
  const ry = r * 0.40;
  const inner = 0.62;

  ctx.beginPath();
  ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
  ctx.ellipse(0, 0, rx * inner, ry * inner, 0, 0, Math.PI * 2, true);

  if (side === 'back') {
    ctx.strokeStyle = 'rgba(245,231,192,0.34)';
    ctx.lineWidth = Math.max(1, r * 0.05);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,243,210,0.55)';
    ctx.lineWidth = Math.max(0.6, r * 0.018);
    ctx.stroke();
  } else {
    ctx.strokeStyle = 'rgba(245,231,192,0.72)';
    ctx.lineWidth = Math.max(1, r * 0.055);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,243,210,0.30)';
    ctx.lineWidth = Math.max(0.6, r * 0.02);
    ctx.stroke();
  }
  ctx.restore();
}

// \u2500\u2500\u2500\u2500 \u5404\u8272\u5185\u542b\u7269 \u2500\u2500\u2500\u2500

function drawMercuryIncl(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, s: { core: string; dark: string }): void {
  // \u73af\u5f62\u5c71
  ctx.save();
  ctx.globalAlpha = 0.4;
  ctx.strokeStyle = hexA(s.core, 0.7);
  ctx.lineWidth = Math.max(0.8, r * 0.025);
  for (const [dx, dy, rr] of [[-0.28, -0.12, 0.20], [0.22, 0.18, 0.15], [0.05, -0.35, 0.10]] as const) {
    ctx.beginPath();
    ctx.arc(cx + dx * r, cy + dy * r, rr * r, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
  // \u5185\u90e8\u6c14\u6ce1
  ctx.save();
  ctx.globalAlpha = 0.30;
  ctx.fillStyle = hexA(s.core, 0.9);
  for (const [dx, dy, rr] of [[0.18, -0.22, 0.07], [-0.1, 0.3, 0.05]] as const) {
    ctx.beginPath();
    ctx.arc(cx + dx * r, cy + dy * r, rr * r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawVenusCore(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, s: { core: string; flow?: string }): void {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 0.66);
  g.addColorStop(0, hexA(s.core, 0.62));
  g.addColorStop(0.45, hexA(s.flow ?? s.core, 0.24));
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.66, 0, Math.PI * 2);
  ctx.fillStyle = g;
  ctx.fill();
  ctx.restore();
}

function drawEarthLand(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, s: { extra?: string; extra2?: string }): void {
  ctx.save();
  ctx.globalAlpha = 0.55;
  ctx.fillStyle = s.extra ?? '#4FA45C';
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.52, cy + r * 0.02);
  ctx.quadraticCurveTo(cx - r * 0.28, cy - r * 0.30, cx - r * 0.02, cy - r * 0.06);
  ctx.quadraticCurveTo(cx + r * 0.12, cy + r * 0.16, cx - r * 0.16, cy + r * 0.30);
  ctx.quadraticCurveTo(cx - r * 0.36, cy + r * 0.34, cx - r * 0.52, cy + r * 0.02);
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(cx + r * 0.24, cy - r * 0.42);
  ctx.quadraticCurveTo(cx + r * 0.46, cy - r * 0.14, cx + r * 0.30, cy + r * 0.10);
  ctx.quadraticCurveTo(cx + r * 0.14, cy - r * 0.06, cx + r * 0.24, cy - r * 0.42);
  ctx.fill();
  ctx.restore();

  // \u6781\u51a0 / \u4e91\u7d6e
  ctx.save();
  ctx.globalAlpha = 0.42;
  ctx.fillStyle = s.extra2 ?? '#F4F9FF';
  ctx.beginPath();
  ctx.ellipse(cx, cy - r * 0.74, r * 0.34, r * 0.11, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(cx - r * 0.1, cy + r * 0.66, r * 0.28, r * 0.09, -0.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawMarsCap(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, s: { extra?: string; dark: string }): void {
  // \u88c2\u7eb9
  ctx.save();
  ctx.globalAlpha = 0.36;
  ctx.strokeStyle = hexA(s.dark, 0.85);
  ctx.lineWidth = Math.max(0.8, r * 0.028);
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.44, cy - r * 0.18);
  ctx.quadraticCurveTo(cx - r * 0.06, cy + r * 0.04, cx + r * 0.10, cy + r * 0.44);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx + r * 0.02, cy - r * 0.44);
  ctx.quadraticCurveTo(cx + r * 0.24, cy - r * 0.06, cx + r * 0.44, cy + r * 0.10);
  ctx.stroke();
  ctx.restore();

  // \u6781\u51a0
  ctx.save();
  ctx.globalAlpha = 0.60;
  ctx.fillStyle = s.extra ?? '#F2F2EF';
  ctx.beginPath();
  ctx.ellipse(cx, cy - r * 0.70, r * 0.30, r * 0.12, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawSaturnBands(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, s: { extra?: string }): void {
  ctx.save();
  ctx.globalAlpha = 0.30;
  ctx.strokeStyle = s.extra ?? '#F5E7C0';
  ctx.lineWidth = Math.max(0.9, r * 0.045);
  for (const dy of [-0.34, -0.05, 0.28]) {
    ctx.beginPath();
    ctx.ellipse(cx, cy + dy * r, r * 0.86, r * 0.18, -0.12, 0.15 * Math.PI, 0.85 * Math.PI);
    ctx.stroke();
  }
  ctx.restore();
}

function drawNeptuneSpot(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, s: { extra?: string }): void {
  // \u79fb\u52a8\u4eae\u65b9\uff08\u975e\u5927\u9ed1\u6591\uff09
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = 0.42;
  ctx.fillStyle = s.extra ?? '#7C8AE8';
  ctx.beginPath();
  ctx.ellipse(cx + r * 0.20, cy + r * 0.10, r * 0.34, r * 0.20, -0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// \u2500\u2500\u2500\u2500 \u5c0f\u5de5\u5177 \u2500\u2500\u2500\u2500

function tracePoly(ctx: CanvasRenderingContext2D, pts: Pt[]): void {
  if (pts.length === 0) return;
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.closePath();
}

/** \u4e03\u8fb9\u5f62\uff08\u51a0\u90e8\u53f0\u9762\u8fd1\u4f3c\uff09 */
function pts7(cx: number, cy: number, r: number): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i < 7; i++) {
    const a = (i * Math.PI * 2) / 7 - Math.PI / 2;
    pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
  }
  return pts;
}

/** \u5341\u516d\u8fdb\u5236\u989c\u8272 \u2192 rgba\uff08\u5e26 alpha\uff09 */
function hexA(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${a})`;
}

/** \u63d0\u4eae\u989c\u8272 */
function lighten(hex: string, k: number): string {
  const h = hex.replace('#', '');
  const n = parseInt(h, 16);
  const r = Math.min(255, Math.round(((n >> 16) & 255) + 255 * k));
  const g = Math.min(255, Math.round(((n >> 8) & 255) + 255 * k));
  const b = Math.min(255, Math.round((n & 255) + 255 * k));
  return `rgb(${r},${g},${b})`;
}
